import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { Button, TextField, Card, Grid, CardHeader, CardContent } from '@mui/material';
import _ from 'underscore';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import {
	SetFormState,
	GetAutocompleteFields,
	ToggleAttachFileIncidentTrackerDialog,
	DownloadAttachment,
	ToggleRemoveFileIncidentTrackerDialog
} from '../../../../actions/IncidentTrackerActions';
import IncidentTrackerComments from './components/IncidentTrackerComments';

const schema = {
	siteId: {
		presence: { allowEmpty: false, message: 'is required' }
	},
	jvcoId: {
		presence: { allowEmpty: false, message: 'is required' }
	},
	date: {
		presence: { allowEmpty: false, message: 'is required' }
	},
	status: {
		presence: { allowEmpty: false, message: 'is required' }
	}
};

const useStyles = makeStyles(theme => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1)
		}
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%'
	},
	divider: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1)
	}
}));

const IncidentTrackerDialogForm = props => {
	const { className, tableId, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.incidentTrackerReducer.formState);
	const dropdowns = useSelector(state => state.incidentTrackerReducer.dropdowns);
	const autocompleteFields = useSelector(
		state => state.incidentTrackerReducer.autocompleteFields
	);
	const comments = useSelector(state => state.incidentTrackerReducer.comments);
	const userId = useSelector(state => state.session.user.id);
	const _hasError = field => !!(formState.touched[field] && formState.errors[field]);
	const [statusClosedDate, setStatusClosedDate] = React.useState('');

	const _handleChange = event => {
		if (event.target.name === 'siteId') {
			dispatch(GetAutocompleteFields(event.target.value));
		}

		if (event.target.name === 'status') {
			if (event.target.value === '8') {
				setStatusClosedDate('');
			} else if (event.target.value === '9') {
				setStatusClosedDate(moment().format('YYYY-MM-DD HH:mm:ss'));
			}
		}

		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});
	};

	const _handleChangeLevels = event => {
		if (event.target.name === 'levelOne') {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === event.target.value)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelTwo: '',
					levelThree: '',
					levelsTwo,
					levelsThree: [],
				},
			});
		} else if (event.target.name === 'levelTwo') {
			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === event.target.value)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value,
					levelThree: '',
					levelsThree,
				},
			});
		} else if (event.target.name === 'levelThree') {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]: event.target.value
				},
			});
		}
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value
			},
			touched: {
				...formState.touched,
				[name]: true
			}
		});
	};

	const _onAddAttachmentClick = () => {
		dispatch(ToggleAttachFileIncidentTrackerDialog(true));
	};

	const _onDownloadAttachmentClick = () => {
		dispatch(DownloadAttachment(userId, formState.values.id));
	};

	const _onRemoveAttachmentClick = () => {
		dispatch(ToggleRemoveFileIncidentTrackerDialog(true));
	};

	useEffect(() => {
		const newTouched = {};

		if (formState.values.submit) {
			_.each(_.keys(schema), item => {
				newTouched[item] = true;
			});
		}

		const errors = validate(formState.values, schema, {
			fullMessages: false
		});

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			}
		});

		if (
			formState.values.closedDate !== undefined
			&& formState.values.closedDate !== null
			&& formState.values.globalStatus === 'Closed'
		) {
			setStatusClosedDate(
				moment(formState.values.closedDate).format(
					'DD/MM/YYYY HH:mm:ss'
				)
			);
		}
	}, [formState.values]);

	useMemo(() => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				jvcoId: autocompleteFields.jvcoId,
				polygon: autocompleteFields.polygon
			}
			// touched: {
			// 	...formState.touched,
			// 	jvcoId: true,
			// },
		});
	}, [autocompleteFields]);

	useMemo(() => {
		if (dropdowns && dropdowns.levelsRelation) {
			// Filtrar
			let levelsTwo = dropdowns.levelsRelation
				.filter(x => x.levelOneId === formState.values.levelOne)
				.map(({ levelTwoId, levelTwoName }) => ({
					value: levelTwoId,
					text: levelTwoName
				}));
			// Distintos
			levelsTwo = [...new Map(levelsTwo.map((item) => [item.value, item])).values()];

			// Filtrar
			let levelsThree = dropdowns.levelsRelation
				.filter(x => x.levelTwoId === formState.values.levelTwo)
				.map(({ levelThreeId, levelThreeName }) => ({
					value: levelThreeId,
					text: levelThreeName
				}));
			// Distintos
			levelsThree = [...new Map(levelsThree.map((item) => [item.value, item])).values()];

			setFormState({
				...formState,
				values: {
					...formState.values,
					levelsTwo,
					levelsThree
				}
			});
		}
	}, [dropdowns]);

	return (
		<div className={classes.fields}>
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					<Card>
						<CardHeader title="Site Information" />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item md={2} xs={2}>
									<TextField
										id="siteId"
										name="siteId"
										label="Site Id"
										variant="outlined"
										error={_hasError('siteId')}
										fullWidth
										helperText={
											_hasError('siteId')
												? formState.errors.siteId[0]
												: null
										}
										value={formState.values.siteId || ''}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="jvcoId"
										name="jvcoId"
										label="JVCO Id"
										variant="outlined"
										error={_hasError('jvcoId')}
										fullWidth
										helperText={
											_hasError('jvcoId')
												? formState.errors.jvcoId[0]
												: null
										}
										value={formState.values.jvcoId || ''}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="polygon"
										name="polygon"
										label="Polygon"
										variant="outlined"
										error={_hasError('polygon')}
										fullWidth
										helperText={
											_hasError('polygon')
												? formState.errors.polygon[0]
												: null
										}
										value={formState.values.polygon || ''}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="area"
										name="area"
										label="Area"
										variant="outlined"
										error={_hasError('area')}
										fullWidth
										helperText={
											_hasError('area')
												? formState.errors.area[0]
												: null
										}
										value={formState.values.area || ''}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="cell"
										name="cell"
										label="Cell"
										variant="outlined"
										error={_hasError('cell')}
										fullWidth
										helperText={
											_hasError('cell')
												? formState.errors.cell[0]
												: null
										}
										value={formState.values.cell || ''}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<SelectField
										id="tech"
										name="tech"
										hintText="Tech"
										defaultValue=""
										menuItems={
											(dropdowns && dropdowns.tech) || []
										}
										value={formState.values.tech || ''}
										onChange={_handleChange}
										error={
											_hasError('tech')
												? formState.errors.tech[0]
												: null
										}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={8} xs={8}>
					<Card>
						<CardHeader title="Incident Status" />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item md={3} xs={3}>
									<SelectField
										id="status"
										name="status"
										hintText="Status"
										defaultValue=""
										menuItems={
											(dropdowns && dropdowns.status)
											|| []
										}
										value={formState.values.status || ''}
										onChange={_handleChange}
										error={
											_hasError('status')
												? formState.errors.status[0]
												: null
										}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<DatePicker
										id="date"
										name="date"
										label="Date"
										style={{ width: '100%' }}
										value={formState.values.date || ''}
										onChange={value => _handleDateChange(value, 'date')}
										minutesStep={1}
										error={
											_hasError('date')
												? formState.errors.date[0]
												: null
										}
										helperText={
											_hasError('date')
												? formState.errors.date[0]
												: null
										}
										format="DD-MM-YYYY"
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="state"
										name="state"
										hintText="State"
										defaultValue=""
										menuItems={
											(dropdowns && dropdowns.state)
											|| []
										}
										value={formState.values.state || ''}
										onChange={_handleChange}
										error={
											_hasError('state')
												? formState.errors.state[0]
												: null
										}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="solutionType"
										name="solutionType"
										hintText="Solution Type"
										defaultValue=""
										menuItems={(dropdowns && dropdowns.solutionType) || []}
										value={formState.values.solutionType || ''}
										onChange={_handleChange}
									/>
								</Grid>
								{/* <Grid item md={8} xs={8}>
									<SelectField
										id="issueDescription"
										name="issueDescription"
										hintText="Issue Description"
										defaultValue=""
										menuItems={(dropdowns && dropdowns.issueDescription) || []}
										value={formState.values.issueDescription || ''}
										onChange={_handleChange}
									/>
								</Grid> */}
								<Grid item md={4} xs={4}>
									<SelectField
										id="levelOne"
										name="levelOne"
										hintText="Level One"
										defaultValue=""
										menuItems={(dropdowns && dropdowns.levelOne) || []}
										value={formState.values.levelOne || ''}
										onChange={_handleChangeLevels}
									/>
								</Grid>
								<Grid item md={4} xs={4}>
									<SelectField
										id="levelTwo"
										name="levelTwo"
										hintText="Level Two"
										defaultValue=""
										menuItems={formState.values.levelsTwo || []}
										value={formState.values.levelTwo || ''}
										onChange={_handleChangeLevels}
									/>
								</Grid>
								<Grid item md={4} xs={4}>
									<SelectField
										id="levelThree"
										name="levelThree"
										hintText="Level Three"
										defaultValue=""
										menuItems={formState.values.levelsThree || []}
										value={formState.values.levelThree || ''}
										onChange={_handleChangeLevels}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>

				<Grid item md={4} xs={4}>
					<Card>
						<CardHeader title="Additional Information" />
						<CardContent>
							<Grid container spacing={2}>
								<Grid item md={6} xs={6}>
									<SelectField
										id="cfnOwner"
										name="cfnOwner"
										hintText="CFN Owner"
										defaultValue=""
										menuItems={
											(dropdowns && dropdowns.cfnOwner)
											|| []
										}
										value={formState.values.cfnOwner || ''}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={6} xs={6}>
									<TextField
										id="incNumber"
										name="incNumber"
										label="Inc Number"
										variant="outlined"
										error={_hasError('incNumber')}
										fullWidth
										helperText={
											_hasError('incNumber')
												? formState.errors.incNumber[0]
												: null
										}
										value={formState.values.incNumber || ''}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={6} xs={6}>
									<TextField
										id="closedDate"
										name="closedDate"
										label="Closed Date"
										variant="outlined"
										InputProps={{
											readOnly: true
										}}
										error={_hasError('closedDate')}
										fullWidth
										helperText={
											_hasError('closedDate')
												? formState.errors.closedDate[0]
												: null
										}
										value={statusClosedDate}
										onChange={_handleChange}
									/>
								</Grid>
								<Grid item md={6} xs={6}>
									<DatePicker
										id="executionDate"
										name="executionDate"
										label="Execution Date"
										style={{ width: '100%' }}
										value={
											formState.values.executionDate || ''
										}
										onChange={value => _handleDateChange(
											value,
											'executionDate'
										)}
										format="DD-MM-YYYY"
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid
					item
					md={12}
					xs={12}
					hidden={!tableId}
				>
					<IncidentTrackerComments tableId={tableId} comments={comments} />
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={12} hidden={!tableId}>
					<Grid container spacing={2}>
						<Grid
							item
							md={2}
							xs={2}
							hidden={formState.values.hasAttachment}
						>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onAddAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
							>
								Attach File
							</Button>
						</Grid>
						<Grid
							item
							md={2}
							xs={2}
							hidden={!formState.values.hasAttachment}
						>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onRemoveAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
							>
								Delete File
							</Button>
						</Grid>
						<Grid item md={2} xs={2}>
							<Button
								className={classes.agreeButton}
								color="primary"
								onClick={_onDownloadAttachmentClick}
								variant="contained"
								size="small"
								style={{ width: '100%' }}
								disabled={!formState.values.hasAttachment}
							>
								Download File
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</div>
	);
};

IncidentTrackerDialogForm.propTypes = {
	className: PropTypes.string,
	tableId: PropTypes.string
};

export default IncidentTrackerDialogForm;
